/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Smarts
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
'use strict'

// maximum number of ancestors to retrieve; used to limit recursion depth
const MAX_NUM_ANCESTORS = 1000

/**
 * This class provides a simple representation of an ontology concept. This is a meta-data holder class, and it is data independent.
 * @author mostafam
 *
 */
export default class Concept {

	/**
	 * Constructor
	 * @param _iri    the concept IRI
	 * @param _name    the concept name
	 */
	constructor(iri, name) {
		// the concept IRI
		this._iri = iri
		// the concept name
		this._name = name
		// the children
		this._children = []
		// list of all parents (super classes)
		this._parents = []
		// the ontology ID
		this._ontologyID = ''
		// the concept visibility
		this._visibility = true
		// concept properties
		this._properties = {}
	}

	/**
	 * add a child to the list of children of this concept
	 * @param child the child to add
	 */
	addChild(child) {
		child && this._children.push(child) && child.addParent(this)
	}

	/**
	 * adds to the list of the parents of this concept, used only by the class itself
	 * @param parent the parent to add
	 */
	addParent(parent) {
		parent && this._parents.push(parent)
	}

	/**
	 * returns the direct parents of this concept, never returns null
	 * @return the direct parents list, can be empty
	 */
	getParents() {
		return this._parents.reduce((subset, parent) => subset.concat(parent.isVisible() ? [parent] : parent.getParents()), [])
	}

	/**
	 * A recursive method that returns the set of ancestors of this concept, never returns undefined
	 * returns a Set in the form of an Array because ancestors may contain duplicates, e.g., a concept that has multiple direct parents which
	 * descend from the same grand parent
	 * @param depth: the current recursion depth
	 * @return the ancestors set, can be empty
	 */
	getAncestors(depth) {
		if (++depth > MAX_NUM_ANCESTORS) {
			return []
		}
		let ancestors = []
		this.getParents().forEach(parent => {
			ancestors.push(parent, ...parent.getAncestors(depth));
		})
		return [...new Set(ancestors)]
	}

	/**
	 * access the children list, never returns null
	 * @return the children list, can be empty
	 */
	getChildren() {
		return !this._children ? [] : this._children
	}

	/**
	 * returns this concept name
	 * @return the concept name
	 */
	getName() {
		return this._name || ''
	}

	/**
	 * @return the IRI
	 */
	getIRI() {
		return this._iri
	}

	/**
	 * @return the ontologyID
	 */
	getOntologyID() {
		return this._ontologyID || ''
	}

	/**
	 * Sets the ontology ID
	 * @param ontologyID
	 */
	setOntologyID(ontologyID) {
		this._ontologyID = ontologyID
	}

	/**
	 * @return concept visibility flag
	 */
	isVisible() {
		return this._isVisible
	}

	/**
	 * Sets the visibility of the concept
	 * @param isVisible visibility flag
	 */
	setVisible(isVisible) {
		this._isVisible = isVisible
	}

	/**
	 * adds a concept property
	 * @param name property name
	 * @param value property value
	 */
	addProperty(name, value) {
		this._properties[name] = value
	}

	/**
	 * returns concept properties
	 * @return concept properties
	 */
	getProperties() {
		return this._properties
	}

}

const UNKNOWN_IRI = 'Unknown'
export const UNKNOWN_CONCEPT = new Concept(UNKNOWN_IRI, '')

